import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import PageWrapper from '../PageWrapper/PageWrapper';
import { PermissionFlags } from '../../components/PermissionFlags/PermissionFlags';
import { Badge } from '../../components/Badge/Badge';
import { ButtonV2 } from '../../components/ButtonV2/ButtonV2';
import { 
  DollarSign, 
  AlertTriangle, 
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Calendar,
  FileText,
  Info,
  Loader2,
  Minus
} from 'lucide-react';

import { motion, AnimatePresence } from 'framer-motion';
import { useGetShops } from '../../hooks/useGetShops';
import FilterPill from '../../components/FilterPill/FilterPill';
import { ShopClient } from '../../api/ShopClientsAPI';
import { Link } from '../../components/Link/Link';
import Card from '../../components/Card/Card';
import NoDataCTA from '../../components/NoDataCTA/NoDataCTA';
import PaymentsOverviewAPI, { 
  PartnerPaymentDto, 
  PaymentOverviewFilters 
} from '../../api/PaymentsOverviewAPI';
import { paymentStatusToTranslationKey } from '../../utils/payment-status';
import { PaymentStatus } from '../../types/payment-statuses';

type Partner = PartnerPaymentDto;
type PaymentOverview = {
  paidAmount: number;
  expectedAmount: number;
  overdueAmount: number;
  currency: string;
};

const generateMonths = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  
  const result = [
    { value: 'overall', label: 'Overall' }
  ];
  
  for (let year = 2024; year <= currentYear + 1; year++) {
    const startMonth = year === 2024 ? 0 : 0;
    const endMonth = year === currentYear + 1 ? currentMonth : 11;
    
    for (let month = startMonth; month <= endMonth; month++) {
      const date = new Date(year, month);
      const monthName = date.toLocaleDateString('en-US', { month: 'long' });
      const monthValue = `${year}-${String(month + 1).padStart(2, '0')}`;
      
      result.push({
        value: monthValue,
        label: `${monthName} ${year}`
      });
    }
  }
  
  return result;
};

const availableMonths = generateMonths();

const mapApiStatusToPaymentStatus = (apiStatus: string): PaymentStatus => {
  switch (apiStatus) {
    case 'paid':
      return PaymentStatus.PAID;
    case 'pending':
      return PaymentStatus.PENDING;
    case 'overdue':
      return PaymentStatus.OVERDUE;
    default:
      return PaymentStatus.UNKNOWN;
  }
};

interface PaymentCardProps {
  title: string;
  paidAmount: number;
  expectedAmount?: number;
  currency: string;
  isOverdue?: boolean;
  t: (key: string) => string;
}

const PaymentCard = ({ 
  title, 
  paidAmount, 
  expectedAmount, 
  currency, 
  isOverdue = false,
  t
}: PaymentCardProps) => {
  
  const safePaidAmount = paidAmount || 0;
  const safeExpectedAmount = expectedAmount || 0;
  
  return (
    <Card>
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {isOverdue ? (
              <AlertTriangle className="h-5 w-5 text-destructive" />
            ) : (
              <DollarSign className="h-5 w-5 text-primary" />
            )}
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            <div className="relative group">
              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                {t('payments_page.overview.currency_info')}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-popover"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <span className="text-2xl font-bold text-foreground">
              {currency} {safePaidAmount.toLocaleString()}
            </span>
            {isOverdue && (
              <Badge variant="destructive" className="text-xs">
                {t('payments_page.overview.overdue')}
              </Badge>
            )}
          </div>
          {expectedAmount !== undefined && safeExpectedAmount > 0 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{t('payments_page.overview.expected')} {currency} {safeExpectedAmount.toLocaleString()}</span>
              <span className={`font-medium ${safePaidAmount >= safeExpectedAmount ? 'text-green-600' : 'text-orange-600'}`}>
                {safePaidAmount >= safeExpectedAmount ? '✓' : '⚠'} {Math.round((safePaidAmount / safeExpectedAmount) * 100)}%
              </span>
            </div>
          )}
          {expectedAmount !== undefined && safeExpectedAmount === 0 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{t('payments_page.overview.expected')} {currency} {safeExpectedAmount.toLocaleString()}</span>
              <span className="font-medium text-gray-500 flex items-center gap-1">
                {safePaidAmount > 0 ? '⚠' : <Minus className="h-3 w-3" />} {safePaidAmount > 0 ? '100%' : '0%'}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

interface PartnerRowProps {
  partner: Partner;
  isExpanded: boolean;
  onToggle: () => void;
  statusFilter: string;
  t: (key: string) => string;
}

const PartnerRow = ({ partner, isExpanded, onToggle, statusFilter, t }: PartnerRowProps) => {
  
  const filteredPayments = partner.payments.filter(payment => {
    if (statusFilter === 'all') return true;
    return payment.status === statusFilter;
  });

  const hasVisiblePayments = filteredPayments.length > 0;

  if (!hasVisiblePayments) return null;

  return (
    <div className="border rounded-lg overflow-hidden">
      <div 
        className="p-4 bg-card hover:bg-accent/50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="p-1 hover:bg-accent rounded">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
            <div>
              <h3 className="font-semibold text-foreground">{partner.name}</h3>
              <p className="text-sm text-muted-foreground">
                {partner.payments.length} {t('payments_page.partners.payments_count')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="text-right">
              <p className="font-medium text-foreground">
                {partner.currency} {partner.totalAmount.toLocaleString()}
              </p>
              <p className="text-muted-foreground">{t('payments_page.partners.total')}</p>
            </div>
            <div className="text-right">
              <p className="font-medium text-green-600">
                {partner.currency} {partner.paidAmount.toLocaleString()}
              </p>
              <p className="text-muted-foreground">{t('payments_page.partners.paid')}</p>
            </div>
            {partner.pendingAmount > 0 && (
              <div className="text-right">
                <p className="font-medium text-orange-600">
                  {partner.currency} {partner.pendingAmount.toLocaleString()}
                </p>
                <p className="text-muted-foreground">{t('payments_page.partners.pending')}</p>
              </div>
            )}
            {partner.overdueAmount > 0 && (
              <div className="text-right">
                <p className="font-medium text-destructive">
                  {partner.currency} {partner.overdueAmount.toLocaleString()}
                </p>
                <p className="text-muted-foreground">{t('payments_page.partners.overdue')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t bg-muted/30"
          >
            <div className="p-4 space-y-3">
              {filteredPayments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex-shrink-0">
                      <Badge 
                        variant={payment.status === 'paid' ? 'default' : payment.status === 'overdue' ? 'destructive' : 'secondary'}
                        className="text-xs"
                      >
                        {t(paymentStatusToTranslationKey(mapApiStatusToPaymentStatus(payment.status)))}
                      </Badge>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{payment.itemName}</p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="font-medium flex items-center gap-1">
                          {payment.currency} {payment.amount > 0 ? payment.amount.toLocaleString() : <Minus className="h-3 w-3" />}
                        </span>
                        {payment.dueDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(payment.dueDate).toLocaleDateString()}
                          </span>
                        )}
                        {payment.invoiceReference && (
                          <span className="flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            {payment.invoiceReference}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ButtonV2
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(payment.orderUrl, '_blank');
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t('payments_page.partners.view_order')}
                  </ButtonV2>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function PaymentsPage() {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState('overall');
  const [searchText, ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedPartners, setExpandedPartners] = useState<Set<string>>(new Set());
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('');
  const [partnerSearchText, setPartnerSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { shops } = useGetShops();

  // Fetch payment overview data
  const fetchPaymentOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: PaymentOverviewFilters = {
        month: selectedMonth,
        status: statusFilter as 'all' | 'paid' | 'pending' | 'overdue',
        partnerId: selectedPartnerId || undefined
      };
      
      const response = await PaymentsOverviewAPI.getPaymentOverview(filters);
      setPartners(response.data.partners);
      
      // Update overview data with currency from API response
      setOverviewData({
        paidAmount: response.data.paidAmount || 0,
        expectedAmount: response.data.expectedAmount || 0,
        overdueAmount: response.data.overdueAmount || 0,
        currency: response.data.currency || 'EUR'
      });
    } catch (err: any) {
      console.error('Error fetching payment overview:', err);
      setError(err.response?.data?.message || t('payments_page.errors.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, statusFilter, selectedPartnerId, t]);

  // Fetch data when filters change
  useEffect(() => {
    fetchPaymentOverview();
  }, [fetchPaymentOverview]);

  // Overview data from API response
  const [overviewData, setOverviewData] = useState<PaymentOverview>({
    paidAmount: 0,
    expectedAmount: 0,
    overdueAmount: 0,
    currency: ''
  });



  const filteredShops = shops.filter(shop => 
    shop.name.toLowerCase().includes(partnerSearchText.toLowerCase())
  );

  // Filter partners by search text only (other filters are handled by API)
  const filteredPartners = partners.filter(partner => 
    partner.name.toLowerCase().includes(searchText.toLowerCase())
  );

  // Check if any partner has payments (to determine if filters returned results)
  const hasAnyPayments = partners.some(partner => partner.payments && partner.payments.length > 0);

  const togglePartnerExpansion = (partnerId: string) => {
    const newExpanded = new Set(expandedPartners);
    if (newExpanded.has(partnerId)) {
      newExpanded.delete(partnerId);
    } else {
      newExpanded.add(partnerId);
    }
    setExpandedPartners(newExpanded);
  };

  const setPartnerFilter = (shop: ShopClient) => {
    setSelectedPartnerId(prev => prev === String(shop.id) ? '' : String(shop.id));
  };

  return (
    <PageWrapper>
      <PermissionFlags requiredFlags={['see-employees']}>
        <div className="space-y-6">
          {/* <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                {t('payments_page.title')}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t('payments_page.description')}
              </p>
            </div>
          </div> */}
          {/* Global Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end">
            <ButtonV2
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
            >
                {showFilters ? t('payments_page.filters.hide') : t('payments_page.filters.show')}
            </ButtonV2>
          </div>
        {showFilters && (
            <Card>
                <div className="space-y-6">
                    {/* Status Filter */}
                  <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{t('payments_page.filters.status')}</span>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="all">{t('payments_page.filters.status.all')}</option>
                        <option value="paid">{t('payments_page.filters.status.paid')}</option>
                        <option value="pending">{t('payments_page.filters.status.pending')}</option>
                        <option value="overdue">{t('payments_page.filters.status.overdue')}</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{t('payments_page.filters.month')}</span>
                      <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        {availableMonths.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Partner Shops Section */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">{t('payments_page.filters.partner_shops')}</h3>
                    <div className="w-full max-w-full">
                      <label>
                        <span className="autocompleteInputWrapper formValue autocomplete">
                          <input
                            className="p-3 w-full border-stone-200 border rounded focus:outline-none"
                            type="text"
                            placeholder={t('payments_page.filters.search_partners') || 'Search partners...'}
                            value={partnerSearchText}
                            onChange={(e) => setPartnerSearchText(e.target.value)}
                            data-dd-action-name={'Partner Search'}
                          />
                        </span>
                      </label>
                    </div>
                    <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                      {
                        filteredShops.length > 0 ? filteredShops.map((s, i) => (
                          <FilterPill 
                            isActive={selectedPartnerId === String(s.id)} 
                            key={i} 
                            text={s.name} 
                            fn={() => setPartnerFilter(s)} 
                          />
                        ))
                        : partnerSearchText ? (
                          <p>{t('payments_page.filters.no_partners_found')}</p>
                        ) : (
                          <>
                            <p>{t('payments_page.filters.no_partners_available')}</p>&nbsp;
                            <Link url="/partners" text={t('payments_page.filters.add_partners')}/>
                          </>
                        )
                      }
                    </div>
                  </div>
                </div>
            </Card>
          )}

          {/* Error Message */}
          {error && (
            <Card>
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                  <p className="font-medium">{t('payments_page.errors.loading_failed')}</p>
                </div>
                <p className="text-sm text-destructive/80 mt-1">{error}</p>
                </div>
            </Card>
          )}

          {/* Overview Cards - Only show when there's data */}
          {hasAnyPayments && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PaymentCard
                title={t('payments_page.overview.paid_vs_expected')}
                paidAmount={overviewData.paidAmount}
                expectedAmount={overviewData.expectedAmount}
                currency={overviewData.currency}
                t={t}
              />
              <PaymentCard
                title={t('payments_page.overview.overdue_amount')}
                paidAmount={overviewData.overdueAmount}
                currency={overviewData.currency}
                isOverdue={true}
                t={t}
              />
            </div>
          )}

          {/* Partners Section */}
          <Card>
            <div>
              {hasAnyPayments && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    <h2 className="text-xl font-semibold text-foreground">{t('payments_page.partners.title')}</h2>
                  </div>
                </div>
              )}
              
              {/* Partners List */}
              <div className="space-y-3">
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
                    <h3 className="text-lg font-medium text-muted-foreground mb-2">
                      {t('payments_page.partners.loading')}
                    </h3>
                  </div>
                ) : hasAnyPayments ? (
                  filteredPartners.map((partner) => (
                    <PartnerRow
                      key={partner.id}
                      partner={partner}
                      isExpanded={expandedPartners.has(partner.id)}
                      onToggle={() => togglePartnerExpansion(partner.id)}
                      statusFilter={statusFilter}
                      t={t}
                    />
                  ))
                ) : (
                  <NoDataCTA
                    headline={t('payments_page.no_results.title') || 'No payment data found'}
                    subtext={t('payments_page.no_results.description') || 'No payment plans match your current filters'}
                    primaryActionText={t('payments_page.no_results.add_payment_plan') || 'Add a payment plan to an Order'}
                    onPrimaryAction={() => window.location.href = '/orders'}
                    secondaryActionText={t('payments_page.no_results.clear_filters') || 'Clear Filters'}
                    onSecondaryAction={() => {
                      setStatusFilter('all');
                      setSelectedMonth('overall');
                      setSelectedPartnerId('');
                    }}
                  />
                )}
              </div>
            </div>
          </Card>
        </div>
      </PermissionFlags>
    </PageWrapper>
  );
}

export default PaymentsPage; 